import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SalovarePhoneInput from '../common/SalovarePhoneInput';
import { BRAND } from '../../theme/brandTokens';

const getFieldSx = (hasError) => ({
  '& .MuiOutlinedInput-root': {
    height: '48px',
    minHeight: '48px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : BRAND.line,
      borderWidth: '2px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : BRAND.accent,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : BRAND.accent,
      borderWidth: '2px',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    fontSize: '16px',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginRight: 0,
  },
});

const StoreContactInfo = ({ formData, updateFormData, errors, setErrors, mode = 'admin' }) => {
  const { t } = useTranslation();
  const isSelfService = mode === 'self-service';
  const formatFieldLabel = (label, required = false) => (required ? `${label} *` : label);

  const clearFieldError = (fieldName) => {
    if (!setErrors || !errors[fieldName]) {
      return;
    }

    setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
  };

  const handleFieldChange = (fieldName, errorField = fieldName) => (event) => {
    updateFormData({ [fieldName]: event.target.value });
    clearFieldError(errorField);
  };

  const renderTextField = ({ id, label, placeholder, value, onChange, error, helperText, type = 'text', required = false }) => {
    const displayLabel = formatFieldLabel(label, required);

    return (
      <>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {displayLabel}
        </Typography>
        <TextField
          id={id}
          fullWidth
          type={type}
          placeholder={placeholder || label}
          value={value}
          onChange={onChange}
          error={!!error}
          helperText={helperText}
          sx={getFieldSx(!!error)}
        />
      </>
    );
  };

  const renderStorePhoneField = ({ required = false } = {}) => (
    <SalovarePhoneInput
      label={isSelfService ? t('storeRegistration.storePhone') : t('storeRegistration.phone')}
      required={required}
      autoCompleteSection="store"
      countryCode={formData.phoneCountryCode}
      number={formData.phoneNumber}
      onCountryCodeChange={(value) => {
        updateFormData({ phoneCountryCode: value });
        clearFieldError('phoneNumber');
      }}
      onNumberChange={(value) => {
        updateFormData({ phoneNumber: value });
        clearFieldError('phoneNumber');
      }}
      placeholder={t('teamMembers.registration.phonePlaceholder')}
      error={!!errors.phoneNumber}
      helperText={errors.phoneNumber}
    />
  );

  if (isSelfService) {
    return (
      <Box id="contact-info-step" sx={{ mt: 2 }}>
        <Typography variant="h5" sx={{ mb: 1, color: BRAND.text, fontWeight: 600 }}>
          {t('storeRegistrationSteps.step2.title')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: BRAND.textMuted }}>
          {t('storeRegistration.contactInfo.selfServiceDescription')}
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ color: BRAND.primaryText, fontWeight: 700 }}>
                  {t('storeRegistration.ownerSectionTitle')}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                {renderTextField({
                  id: 'store-registration-owner-name-input',
                  label: t('storeRegistration.ownerName'),
                  value: formData.ownerName,
                  onChange: handleFieldChange('ownerName'),
                  error: errors.ownerName,
                  helperText: errors.ownerName,
                  required: true,
                })}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                {renderTextField({
                  id: 'store-registration-owner-email-input',
                  label: t('storeRegistration.ownerEmail'),
                  value: formData.ownerEmail,
                  onChange: handleFieldChange('ownerEmail'),
                  error: errors.ownerEmail,
                  helperText: errors.ownerEmail,
                  type: 'email',
                  required: true,
                })}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <SalovarePhoneInput
                  label={t('storeRegistration.ownerPhone')}
                  required
                  autoCompleteSection="owner"
                  countryCode={formData.ownerPhoneCountryCode}
                  number={formData.ownerPhoneNumber}
                  onCountryCodeChange={(value) => {
                    updateFormData({ ownerPhoneCountryCode: value });
                    clearFieldError('ownerPhoneNumber');
                  }}
                  onNumberChange={(value) => {
                    updateFormData({ ownerPhoneNumber: value });
                    clearFieldError('ownerPhoneNumber');
                  }}
                  placeholder={t('teamMembers.registration.phonePlaceholder')}
                  error={!!errors.ownerPhoneNumber}
                  helperText={errors.ownerPhoneNumber}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ borderTop: `1px solid ${BRAND.line}`, pt: 3, mt: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ color: BRAND.primaryText, fontWeight: 700 }}>
                    {t('storeRegistration.storeContactSectionTitle')}
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                {renderTextField({
                  id: 'store-registration-store-email-input',
                  label: t('storeRegistration.storeEmail'),
                  value: formData.email,
                  onChange: handleFieldChange('email'),
                  error: errors.email,
                  helperText: errors.email,
                  type: 'email',
                })}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                {renderStorePhoneField()}
              </Grid>

              <Grid size={{ xs: 12 }}>
                {renderTextField({
                  id: 'store-registration-website-input',
                  label: t('storeRegistration.website'),
                  value: formData.website,
                  onChange: handleFieldChange('website'),
                })}
              </Grid>

              <Grid size={{ xs: 12 }}>
                {renderTextField({
                  id: 'store-vat-number-input',
                  label: t('vat.fields.number'),
                  value: formData.vatNumber || '',
                  onChange: handleFieldChange('vatNumber'),
                  helperText: errors.vatNumber || t('vat.help.number'),
                  error: errors.vatNumber,
                })}
              </Grid>

              <Grid size={{ xs: 12 }}>
                {renderTextField({
                  id: 'store-registration-google-review-url-input',
                  label: t('storeRegistration.googleReviewUrl'),
                  placeholder: t('storeRegistration.googleReviewUrlPlaceholder'),
                  value: formData.googleReviewUrl || '',
                  onChange: handleFieldChange('googleReviewUrl'),
                  helperText: errors.googleReviewUrl || t('storeRegistration.googleReviewUrlHelp'),
                  error: errors.googleReviewUrl,
                })}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box id="contact-info-step" sx={{ mt: 2 }}>
      <Typography variant="h5" sx={{ mb: 1, color: BRAND.text, fontWeight: 600 }}>
        {t('storeRegistrationSteps.step2.title')}
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, color: BRAND.textMuted }}>
        {t('storeRegistrationSteps.step2.description')}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderTextField({
                id: 'store-registration-email-input',
                label: t('storeRegistration.email'),
                value: formData.email,
                onChange: handleFieldChange('email'),
                error: errors.email,
                helperText: errors.email,
                type: 'email',
                required: true,
              })}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              {renderStorePhoneField({ required: true })}
            </Grid>

            <Grid size={{ xs: 12 }}>
              {renderTextField({
                id: 'store-registration-website-input',
                label: t('storeRegistration.website'),
                value: formData.website,
                onChange: handleFieldChange('website'),
              })}
            </Grid>

            <Grid size={{ xs: 12 }}>
              {renderTextField({
                id: 'store-vat-number-input',
                label: t('vat.fields.number'),
                value: formData.vatNumber || '',
                onChange: handleFieldChange('vatNumber'),
                helperText: errors.vatNumber || t('vat.help.number'),
                error: errors.vatNumber,
              })}
            </Grid>

            <Grid size={{ xs: 12 }}>
              {renderTextField({
                id: 'store-registration-google-review-url-input',
                label: t('storeRegistration.googleReviewUrl'),
                placeholder: t('storeRegistration.googleReviewUrlPlaceholder'),
                value: formData.googleReviewUrl || '',
                onChange: handleFieldChange('googleReviewUrl'),
                helperText: errors.googleReviewUrl || t('storeRegistration.googleReviewUrlHelp'),
                error: errors.googleReviewUrl,
              })}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StoreContactInfo;
